import React, { useEffect, useState } from "react";
import { Upload as AntUpload } from "antd";
import type { UploadProps } from "antd";
import { FormInstance } from "antd/es/form";
import { getCsrfToken } from "@/components/common/utils/utils";
import { ScaleInput } from "@/components/common/scale-form";

// Define prop types for the FileUpload component
interface FileUploadProps {
    className?: string;
    label: string;
    name: string;
    form: FormInstance;
    accept?: string;
    dark: boolean;
    onSuccess?: (response: UploadedFileResponse) => void;
    onFileUploaded?: (uploadId: string) => void;
}

// Define the structure of the server response after file upload
interface UploadedFileResponse {
    id: string;
    originalFileName: string;
    relativePath: string;
    extension: string;
}

const FileUpload: React.FC<FileUploadProps> = ({
                                                   label,
                                                   className,
                                                   name,
                                                   form,
                                                   accept,
                                                   dark,
                                                   onSuccess,
                                                   onFileUploaded
                                               }) => {
    const [csrfToken, setCsrfToken] = useState("");
    const [isUploaded, setIsUploaded] = useState(false);
    const [uploading, setUploading] = useState(false);

    // Fetch CSRF token on component mount
    useEffect(() => {
        setCsrfToken(getCsrfToken());

        // Check if the field already has a value and set uploaded state accordingly
        const existingId = form.getFieldValue(`${name}_id`);
        if (existingId) {
            setIsUploaded(true);
        }
    }, [form, name]);

    // Handle file upload using Ant Design Upload
    const uploadProps: UploadProps = {
        name: 'file',
        action: '/api/files/upload',
        headers: {
            'X-CSRF-TOKEN': csrfToken,
        },
        accept: accept,
        maxCount: 1,
        showUploadList: false,
        beforeUpload: () => {
            setUploading(true);
            return true;
        },
        onChange: (info) => {
            if (info.file.status === 'done') {
                setUploading(false);
                const response = info.file.response as UploadedFileResponse;

                if (!response || !response.id) {
                    console.error("Invalid response: no ID returned");
                    return;
                }

                const values = {
                    [name]: response.originalFileName,
                    [`${name}_id`]: response.id,
                };

                console.log("File uploaded:", values);

                // Update form fields
                form.setFieldsValue(values);

                // Set uploaded state to true
                setIsUploaded(true);

                // Update store state through callbacks
                setTimeout(() => {
                    // If there's a success callback, call it with the response
                    if (onSuccess) {
                        onSuccess(response);
                    }

                    // If there's a file uploaded callback and we have an ID, call it
                    if (response.id && onFileUploaded) {
                        console.log(`Calling onFileUploaded with ID: ${response.id}`);
                        onFileUploaded(response.id);
                    }

                    // Force form validation for these fields
                    form.validateFields([name, `${name}_id`]).catch(() => {});
                }, 100);
            } else if (info.file.status === 'error') {
                setUploading(false);
                console.error("Upload failed:", info.file.error);
            }
        },
    };

    return (
        <div className={`flex items-center ${dark ? 'dark-mode' : ''}`}>
            <label className={`w-1/3 pr-4 text-right ${dark ? 'text-white' : ''}`} htmlFor={name}>
                {label}
            </label>
            <div className="w-2/3">
                <AntUpload
                    {...uploadProps}
                    className={`${className} ${dark ? 'dark-mode-upload' : ''}`}
                >
                    <button type="button" className={`border rounded p-2 ${dark ? 'border-gray-600 text-gray-200' : 'border-gray-300'}`}>
                        {uploading ? 'Uploading...' : 'Click to Upload'}
                    </button>
                </AntUpload>

                <ScaleInput
                    readOnly
                    hidden
                    rules={[{ required: true }]}
                    key={name}
                    name={name}
                    label={label}
                    dark={dark}
                />

                <ScaleInput
                    hidden
                    readOnly
                    key={`${name}_id`}
                    name={`${name}_id`}
                    label={`${label}_id`}
                    rules={[
                        {
                            required: true,
                            message: "File ID is required",
                        },
                    ]}
                    dark={dark}
                />

                {/* Success message that only shows when isUploaded is true */}
                {isUploaded && (
                    <div className="mt-2 p-2 bg-green-50 text-green-700 rounded text-sm">
                        <span className="mr-1">✓</span>
                        {label} uploaded successfully!
                    </div>
                )}
            </div>
        </div>
    );
};

export default FileUpload;